// src/cmps/TextEditor/plugins/LinkPlugin.jsx
import { useState, useEffect, useCallback } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getSelection,
  $isRangeSelection,
  SELECTION_CHANGE_COMMAND,
} from 'lexical'
import { mergeRegister } from '@lexical/utils'
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { LinkPlugin as LexicalLinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { useFloatingEditorPanel } from '../utils/useFloatingEditorPanel'

export function LinkPlugin({ anchorElem }) {
  const [editor] = useLexicalComposerContext()
  const [url, setUrl] = useState('')
  const {
    open,
    setOpen,
    refs,
    floatingStyles,
    getFloatingProps,
    updateFromSelection,
  } = useFloatingEditorPanel({
    placement: 'bottom-start',
    boundary: anchorElem?.current ?? anchorElem ?? document.body,
  })

  /** ❶ פותח ידנית מה-Toolbar */
  const openManually = useCallback(() => {
    setOpen(true)
    requestAnimationFrame(updateFromSelection) // wait layout
  }, [setOpen, updateFromSelection])

  /** ❷ בדיקה אם הסמן על-גבי קישור קיים */
  const refreshBySelection = useCallback(() => {
    editor.getEditorState().read(() => {
      const sel = $getSelection()
      if (!$isRangeSelection(sel)) {
        setOpen(false)
        return
      }

      const node = sel.getNodes()[0]
      const parent = node.getParent()
      const link = $isLinkNode(parent)
        ? parent
        : $isLinkNode(node)
        ? node
        : null

      if (link) {
        setUrl(link.getURL())
        setOpen(true)
        updateFromSelection()
      } else {
        setOpen(false)
      }
    })
  }, [editor, updateFromSelection])

  /** רישום commands + listeners */
  useEffect(
    () =>
      mergeRegister(
        // שינוי selection
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            refreshBySelection()
            return false
          },
          1
        ),
        // פתיחה ידנית מה-Toolbar / FloatingToolbar
        editor.registerCommand(
          'SHOW_LINK_EDITOR',
          () => {
            openManually()
            return true
          },
          0
        ),
        // כל Update אחר (undo/redo וכו')
        editor.registerUpdateListener(refreshBySelection)
      ),
    [editor, refreshBySelection, openManually]
  )

  /* apply / remove link */
  const save = () => {
    editor.update(() =>
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url || null)
    )
    setOpen(false)
  }
  const remove = () => {
    editor.update(() => editor.dispatchCommand(TOGGLE_LINK_COMMAND, null))
    setOpen(false)
  }

  /* ---------- UI ---------- */
  return (
    <>
      <LexicalLinkPlugin /> {/* עדיין צריך את transform-decorator הפנימי */}
      {open && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className="z-50 w-80 rounded-lg bg-zinc-900 border border-zinc-700 p-4 shadow-xl"
        >
          <label className="mb-2 block text-sm text-white">כתובת קישור:</label>
          <input
            className="w-full rounded-md border border-zinc-600 bg-zinc-800 px-3 py-2 text-sm text-white"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
            autoFocus
          />
          <div className="mt-4 flex gap-2">
            <button
            type='button'
              onClick={remove}
              className="flex-1 rounded-md bg-red-600  py-1.5 text-sm text-white"
            >
              הסר
            </button>
            <button
            type='button'
              onClick={save}
              className="flex-1 rounded-md bg-teal-600 py-1.5 text-sm text-white"
            >
              {url ? 'שמור' : 'צור'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
